import type { ParseKeys, TFunction } from 'i18next'

import {
  pricingAlreadyReady,
  pricingNotRequested,
  quoteAlreadyConverted,
  quoteMoneyNotBookable,
  quoteNotConvertible,
} from '@/features/quotes/api/useQuote'
import { isCreditTerms } from '@/features/quotes/quoteDraft'
import {
  recipientRefusal,
  type RecipientState,
} from '@/features/quotes/quoteReadiness'
import { ApiError } from '@/lib/api'
import { paymentOptionLabel } from '@/lib/paymentOptions'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'
import { rateMissingMessage, refusalMessage } from '@/lib/refusals'
import type { ChecklistIssue } from '@/types/domain'

/* Server facts turned into the reader's sentence.
 *
 * Both of these were duplicated between the detail page and the sections it was
 * split into. A module rather than a component file because neither returns
 * markup — and because a component file may only export components.
 */

/** A server refusal as one line, in the viewer's language.
 *
 * Through describeProblem rather than the problem's own title or detail
 * (AD-22, AD-27): those are written server-side in English for a log, and a
 * Russian user meeting a rejected save is the last person who should be handed
 * a second language. */
export function errorMessage(error: unknown, t: TFunction): string {
  return t(describeProblem(error).detailKey)
}

/** The send's refusal, with the one case the generic mapping cannot word.
 *
 * The name is the offer sheet's; the wording is not, and no longer lives here.
 * A send refused because the issuing entity's branding is incomplete (FR-64) is
 * the same refusal a case document and a client invoice can be answered with —
 * three sheets, one server shape, one sentence — so the reader of that shape sits
 * in `lib/refusals.ts` and this is the quote surface's name for it. Kept as a
 * name rather than collapsed into the call so that the two quote pages holding
 * it keep importing their wording from the module that words everything else
 * they show. */
export function sendErrorMessage(error: unknown, t: TFunction): string {
  return refusalMessage(error, t)
}

/** The conversion's refusal, in the sheet that asked for it (QW-11).
 *
 * Three refusals arrive as `conflict` with a DETAIL token, and the generic
 * conflict sentence — somebody changed the record, reload it — is the wrong
 * advice for all three: one means the case already exists, one that the quote
 * is no longer waiting on the client's answer, one that its figures cannot
 * become money. Each gets its own words. So does a missing exchange rate,
 * worded from its currency and day by the shared `rateMissingMessage`.
 * Everything else goes through the suffix mapping untouched, including the
 * deactivated issuing entity, whose suffix of its own already names Settings. */
export function convertErrorMessage(error: unknown, t: TFunction): string {
  // The case exists, but GET /quotes/:id serves its link only to a caller who
  // can reach the case — one who cannot gets no caseId — so the sentence names
  // the sidebar section instead, by the sidebar's own key, rather than promising
  // a link the page may never draw.
  if (quoteAlreadyConverted(error))
    return t('quotes.convert.errors.alreadyConverted', { section: t('nav.cases') })
  if (quoteNotConvertible(error)) return t('quotes.convert.errors.notConvertible')
  if (quoteMoneyNotBookable(error)) return t('quotes.convert.errors.moneyNotBookable')
  // The conversion books the case's agreed rate and the quote's costs, so it is
  // refused with `rate-missing` like any money write (conversion_handler.go);
  // only the problem's own members say whether to wait or to ask.
  return rateMissingMessage(error, t) ?? errorMessage(error, t)
}

/** The conversion's refusal of the chosen Operations owner, worded for the
 *  picker it lands under (S-1), or undefined when the refusal is not about the
 *  owner.
 *
 *  Three codes on `operationsOwnerId` (conversion_handler.go `fail`): nobody
 *  was chosen; the account is deactivated or the id names nobody; the account
 *  may not own a case (no `case.operations.lead`, or a `cases` scope of `own`).
 *  Each names what to do — choose somebody else — which is why they sit under
 *  the field, and why the page's banner keeps these words once the sheet is
 *  closed. `invalid` is left to the banner's generic sentence: the picker only
 *  ever sends an id the server served, so an unparseable one is not something
 *  the reader can fix by choosing again. */
export function convertOwnerError(error: unknown, t: TFunction): string | undefined {
  switch (fieldErrors(error)['operationsOwnerId']?.[0]) {
    case 'required':
      return t('quotes.convert.owner.errors.required')
    case 'not-assignable':
      return t('quotes.convert.owner.errors.notAssignable')
    case 'not-operations':
      return t('quotes.convert.owner.errors.notOperations')
    default:
      return undefined
  }
}

/** The server's refusal of the credit days (S-12), worded for the field it
 *  lands under, or undefined when the refusal is about something else. Two
 *  codes on `paymentTermsCreditDays` — the header PUT's and the conversion's
 *  alike: `required` (credit terms with no days) and `invalid` (outside 1–365,
 *  or days beside other terms). */
export function creditDaysError(error: unknown, t: TFunction): string | undefined {
  const code = fieldErrors(error)['paymentTermsCreditDays']?.[0]
  return code ? t(fieldErrorKey(code)) : undefined
}

/** A quote's payment terms in the reader's words: one of the workbook's five
 *  as its label, anything else as typed, and a credit term with its days
 *  (S-12) — «Кредит, 30 дней». Undefined when the quote states none. */
export function paymentTermsText(
  record: { paymentTerms?: string; paymentTermsCreditDays?: number },
  t: TFunction,
): string | undefined {
  if (!record.paymentTerms) return undefined
  const label = paymentOptionLabel(record.paymentTerms, (key, fallback) =>
    t(key as 'quotes.paymentOptions.credit', fallback),
  )
  return isCreditTerms(record.paymentTerms) && record.paymentTermsCreditDays !== undefined
    ? t('quotes.form.creditDaysValue', { terms: label, count: record.paymentTermsCreditDays })
    : label
}

/** The pricing request's refusal of the chosen colleague, worded for the
 *  picker it lands under (S-2a), or undefined when the refusal is not about
 *  the assignee.
 *
 *  Three codes on `assigneeId` (quote/pricing_service.go): nobody was chosen;
 *  the account cannot sign in or names nobody; the account may not price
 *  quotes (no `quote.pricing.provide`, or a `quotes` scope of `own`). `invalid`
 *  stays with the banner for convertOwnerError's reason — the picker only
 *  sends ids the server served. */
export function pricingAssigneeError(error: unknown, t: TFunction): string | undefined {
  switch (fieldErrors(error)['assigneeId']?.[0]) {
    case 'required':
      return t('quotes.pricingState.errors.required')
    case 'not-assignable':
      return t('quotes.pricingState.errors.notAssignable')
    case 'not-pricing':
      return t('quotes.pricingState.errors.notPricing')
    default:
      return undefined
  }
}

/** The request's refusal of the note, under the note field: `too-long` past
 *  500 characters. Any other code on it is the generic field sentence. */
export function pricingNoteError(error: unknown, t: TFunction): string | undefined {
  const code = fieldErrors(error)['note']?.[0]
  if (code === undefined) return undefined
  return code === 'too-long'
    ? t('quotes.pricingState.errors.noteTooLong')
    : t(fieldErrorKey(code))
}

/** A pricing act's refusal as the banner's one line (S-2a).
 *
 *  The two state conflicts are DETAIL tokens on a `conflict` problem
 *  (`pricing-already-ready`, `pricing-not-requested`) and get their own words:
 *  the generic conflict sentence says "reload", and the hook has already
 *  re-read the quote. A quote that stopped being a draft is the third conflict
 *  and says what the Edit button says of a sent quote. Everything else goes
 *  through the suffix mapping. */
export function pricingErrorMessage(error: unknown, t: TFunction): string {
  if (pricingAlreadyReady(error)) return t('quotes.pricingState.errors.alreadyReady')
  if (pricingNotRequested(error)) return t('quotes.pricingState.errors.notRequested')
  if (error instanceof ApiError && error.status === 409) {
    return t('quotes.revisions.notEditable')
  }
  return errorMessage(error, t)
}

/** Why the Send button is refusing, or undefined when it is not.
 *
 * A disabled control that does not say why is a dead end: the reader can see
 * that the offer will not go and has nothing to act on. Every Send on the
 * detail page — the section bar's, the «Предпросмотр» section's and the
 * preview sheet's — asks this, so the wording cannot live at any one call site
 * or they would drift apart on the one question this screen exists to answer.
 *
 * The GATE is not decided here. `sendable` is the server's (FR-3); this only
 * words the refusal, from the facts the server's own send reads, in the
 * server's order (`quote.Service.Send`): a reader who may not read the client's
 * contacts is refused before anything else is looked at; then the quote's own
 * state, which is what `sendable` answers (a draft, with cargo, its checklist
 * closed); then the address the offer would go to, which `sendable` does NOT
 * include — a quote the server has cleared can still have nowhere to go. Where
 * the server refuses for a reason those facts cannot see, the generic line is
 * what remains true — never a guess at a cause.
 *
 * The blocking cases are told apart because they need different actions: an
 * empty quote wants positions added, an open checklist wants the positions it
 * already has completed, a missing address wants the directory, and a reader
 * without contact access wants a colleague. Sending the reader to "add cargo"
 * on a quote that has four rows would be worse than saying nothing.
 *
 * The cargo strings are the preview sheet's and the recipient strings the send
 * sheet's, deliberately: a button whose tooltip disagreed with the sheet one
 * press away would leave the reader deciding which to believe. */
export function sendBlockedReason(
  { sendable, hasCargo, blocked, recipient }: {
    sendable: boolean
    hasCargo: boolean
    blocked: boolean
    recipient: RecipientState
  },
  t: TFunction,
): string | undefined {
  if (recipient.kind === 'noContactAccess') return recipientRefusal(recipient, t)
  if (!sendable) {
    if (!hasCargo) return t('quotes.preview.blockedNoCargo')
    if (blocked) return t('quotes.preview.blockedChecklist')
    return t('quotes.preview.blockedGeneric')
  }
  return recipientRefusal(recipient, t)
}

/* The checklist arrives as codes and a position number; the sentence is built
 * here. The server owns WHICH issues exist and what each blocks — that rule has
 * to come from one place — but the words are this layer's, because it is the
 * layer that knows the reader works in Russian.
 *
 * A position with no number yet takes the unnumbered wording rather than
 * rendering "position undefined" at somebody. */
export function issueText(issue: ChecklistIssue, t: TFunction): string {
  const suffix = issue.position ? '' : '_noPosition'
  /* Widened to the union of its own possible values before the call. The
   * template literal is already exactly that union, but `t` resolves its
   * overloads differently for a union key passed alongside an options object,
   * and picks one that accepts neither. Naming the type keeps every key checked
   * against the catalogue — a misspelling here still fails the build. */
  const key = `quotes.checklist.issue.${issue.code}${suffix}` as ParseKeys
  return t('quotes.checklist.line', {
    issue: t(key, {
      position: issue.position,
    }),
    blocks: t(`quotes.checklist.blocks.${issue.blocks}`),
  })
}

/** The letter the next revision will carry: A, B, … Z, AA.
 *
 * Mirrors RevisionLabel in the backend, and is shown rather than relied upon —
 * the server mints the real one inside the transaction, so this is a preview.
 * Divergence would be cosmetic and the server always wins. Read by the send
 * sheet and by the quote page's preview section. */
export function nextRevisionLabel(issued: number): string {
  let ordinal = issued + 1
  let label = ''
  while (ordinal > 0) {
    ordinal -= 1
    label = String.fromCharCode(65 + (ordinal % 26)) + label
    ordinal = Math.floor(ordinal / 26)
  }
  return label
}
